import React from "react";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import ThemeToggle from "@/components/ThemeToggle";
import i18n from "@/lib/i18n";
import SpotCarLogo from "@/components/SpotCarLogo";

const LOCALES = [
    { value: "en", label: "EN" },
    { value: "cs", label: "CS" },
    { value: "sk", label: "SK" },
    { value: "de", label: "DE" },
    { value: "pl", label: "PL" },
    { value: "fr", label: "FR" },
    { value: "it", label: "IT" },
    { value: "es", label: "ES" },
    { value: "uk", label: "UK" },
    { value: "ru", label: "RU" },
];

export default function Nav() {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);

    const links = [
        { key: "how-it-works", label: t("landing.nav.howItWorks"), href: "#how" },
        { key: "features", label: t("landing.nav.features"), href: "#features" },
        { key: "pricing", label: t("landing.nav.pricing"), href: "#pricing" },
        { key: "faq", label: t("landing.nav.faq"), href: "#faq" },
    ];

    return (
        <header
            className="sticky top-0 z-50 border-b-2 border-black bg-white !rounded-none"
            data-testid="site-header"
        >
            <div className="flex items-stretch">
                {/* Logo */}
                <button
                    data-testid="logo-link"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label="Scroll to top"
                    className="flex items-center gap-3 border-r-2 border-black px-6 py-4 bg-transparent border-t-0 border-b-0 border-l-0 cursor-pointer text-inherit"
                >
                    <SpotCarLogo size={32} />
                    <span className="font-display text-xl uppercase tracking-tighter">
                        SpotCar
                    </span>
                </button>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-stretch ml-auto">
                    {links.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            data-testid={`nav-link-${l.key}`}
                            className="self-stretch flex items-center border-l-2 border-black px-8 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[var(--surface-container-highest)] transition-colors duration-100 focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="/sign-in"
                        data-testid="nav-signin"
                        className="self-stretch flex items-center border-l-2 border-black bg-[#FFCC00] px-8 font-mono text-xs uppercase tracking-[0.2em] text-black hover:bg-black hover:text-[#FFCC00] transition-colors duration-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                    >
                        {t("landing.nav.signIn")}
                    </a>
                    <div className="border-l-2 border-black self-stretch flex items-center">
                        <ThemeToggle variant="nav" testid="nav-theme-toggle" />
                    </div>
                    <div className="border-l-2 border-black self-stretch flex items-center relative">
                        <select
                            value={i18n.language}
                            onChange={(e) => {
                                i18n.changeLanguage(e.target.value);
                                localStorage.setItem("spotcar-locale", e.target.value);
                            }}
                            data-testid="nav-lang-select"
                            aria-label="Change language"
                            className="self-stretch appearance-none pl-6 pr-10 bg-transparent font-mono text-xs uppercase tracking-[0.2em] cursor-pointer border-none outline-none focus:outline-none"
                        >
                            {LOCALES.map((l) => (
                                <option key={l.value} value={l.value}>{l.label}</option>
                            ))}
                        </select>
                        <svg className="pointer-events-none absolute right-4 w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5l3-3 3 3" /><path d="M3 7l3 3 3-3" /></svg>
                    </div>
                    <a
                        href="/sign-up"
                        data-testid="nav-cta-get-started"
                        className="self-stretch flex items-center border-l-2 border-black bg-black px-8 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FFCC00] hover:text-black transition-colors duration-100 focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                    >
                        {t("landing.nav.getStarted")}
                    </a>
                </nav>

                {/* Mobile toggle */}
                <div className="md:hidden flex items-stretch">
                    <div className="border-l-2 border-black flex items-center">
                        <select
                            value={i18n.language}
                            onChange={(e) => {
                                i18n.changeLanguage(e.target.value);
                                localStorage.setItem("spotcar-locale", e.target.value);
                            }}
                            data-testid="mobile-nav-lang-select"
                            aria-label="Change language"
                            className="self-stretch px-4 bg-transparent font-mono text-xs uppercase tracking-[0.2em] cursor-pointer border-none outline-none focus:outline-none"
                        >
                            {LOCALES.map((l) => (
                                <option key={l.value} value={l.value}>{l.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="border-l-2 border-black flex items-center">
                        <ThemeToggle variant="nav" testid="mobile-nav-theme-toggle" />
                    </div>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="border-l-2 border-black px-5 py-4"
                        aria-label="Toggle menu"
                        aria-expanded={open}
                        aria-controls="mobile-menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div id="mobile-menu" role="dialog" className="md:hidden border-t-2 border-black bg-white">
                    {links.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-nav-${l.key}`}
                            className="block border-b-2 border-black px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[var(--surface-container-highest)] focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="/sign-in"
                        onClick={() => setOpen(false)}
                        data-testid="mobile-nav-sign-in"
                        className="block border-b-2 border-black bg-[#FFCC00] px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black hover:bg-black hover:text-[#FFCC00] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                    >
                        {t("landing.nav.signIn")}
                    </a>
                    <a
                        href="/sign-up"
                        onClick={() => setOpen(false)}
                        data-testid="mobile-nav-cta"
                        className="block bg-black px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white active:bg-[#FFCC00] active:text-black focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                    >
                        {t("landing.nav.getStarted")}
                    </a>
                </div>
            )}
        </header>
    );
}
